import { inject, injectable } from "inversify";
import type * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { DomElementsService, EventDispatcher, Logger } from "@designboticteam/core";
import { CameraService } from "../../stage/CameraService";
import type { ICameraControlService } from "./ICameraControlService";

@injectable()
class OrbitCameraControlService implements ICameraControlService {
	readonly serviceName = "🎥 OrbitCameraControlService";
	private logger = new Logger(`🎬 WORKSPACE 3D | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#0066cc",
	});

	public readonly onCameraUpdated = new EventDispatcher<{ camera: THREE.PerspectiveCamera }>("OrbitCameraControlService.onCameraUpdated");
	public readonly onCameraControlsStatusChanged = new EventDispatcher<{ isEnabled: boolean }>("OrbitCameraControlService.onCameraControlsStatusChanged");

	#controls: OrbitControls | null = null;
	public get controls(): OrbitControls {
		if (this.#controls === null) {
			throw new Error("Controls not initialized");
		}

		return this.#controls;
	}

	constructor(
		@inject(CameraService) private cameraService: CameraService,
		@inject(DomElementsService) private domElementsService: DomElementsService,
	) {
		this.logger.log("constructed");
	}

	public initialize(): void {
		const camera = this.cameraService.camera;
		const container = this.domElementsService.containerElement;

		this.#controls = new OrbitControls(camera, container);
		this.controls.enableDamping = false;
		this.controls.screenSpacePanning = false;

		this.controls.addEventListener("change", () => {
			this.onCameraUpdated.dispatch({ camera });
		});
	}

	public get targetPoint(): THREE.Vector3 {
		return this.controls.target;
	}

	public setTargetPoint(target: THREE.Vector3): void {
		this.controls.target.copy(target);
		this.controls.update();
	}

	public enable(): void {
		this.controls.enabled = true;
		this.onCameraControlsStatusChanged.dispatch({ isEnabled: true });
	}

	public disable(): void {
		this.controls.enabled = false;
		this.onCameraControlsStatusChanged.dispatch({ isEnabled: false });
	}

	public dispose(): void {
		this.onCameraUpdated.clear();
		this.controls.dispose();
		this.#controls = null;
	}
}

export { OrbitCameraControlService };
